"use client";

import { IconHeading } from "@/components/platform/v2/IconHeading";
import { useEffect, useState } from "react";
//import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/platform/v2/ui/dialog";
import { Input } from "@/components/platform/v2/ui/input";
import { Label } from "@/components/platform/v2/ui/label";
import { Textarea } from "@/components/platform/v2/ui/textarea";
import { Button } from "@/components/platform/v2/ui/button";
import { CheckCircle2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

export type OfferType =
  | "financial"
  | "strategic"
  | "corporate"
  | "market"
  | "other";

/*const schema = z.object({
  name: z.string().trim().min(2, "Укажите имя").max(100),
  company: z.string().trim().max(150).optional().or(z.literal("")),
  role: z.string().trim().min(2, "Укажите вашу роль").max(120),
  range: z.string().trim().max(120).optional().or(z.literal("")),
  interest: z.string().trim().min(10, "Расскажите, что вас заинтересовало").max(1500),
  contact: z.string().trim().min(5, "Укажите контакт").max(255),
  link: z.string().trim().max(300).optional().or(z.literal("")),
});*/

const empty = {
  name: "",
  company: "",
  role: "",
  range: "",
  interest: "",
  contact: "",
  link: "",
};

const offerOptions: { value: OfferType; label: string }[] = [
  { value: "financial", label: "Финансовая инвестиция" },
  { value: "strategic", label: "Стратегическое партнёрство" },
  { value: "corporate", label: "Корпоративный инвестор" },
  { value: "market", label: "Совместный рынок / канал продаж" },
  { value: "other", label: "Другое" },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultOffer?: OfferType;
  defaultWantMaterials?: boolean;
}

const InvestorContactDialog = ({
  open,
  onOpenChange,
  defaultOffer = "financial",
  defaultWantMaterials = true,
}: Props) => {
  const [form, setForm] = useState(empty);
  const [offer, setOffer] = useState<OfferType>(defaultOffer);
  const [state, setState] = useState<"idle" | "sending" | "success">("idle");

  useEffect(() => {
    if (open) {
      setOffer(defaultOffer);
      setState("idle");
      setForm(empty);
    }
  }, [open, defaultOffer, defaultWantMaterials]);

  const set =
    (key: keyof typeof empty) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  /*const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast({
        title: "Проверьте данные",
        description: parsed.error.issues[0].message,
        variant: "destructive",
      });
      return;
    }
    setState("sending");
    try {
      const stored = JSON.parse(localStorage.getItem("investorRequests") || "[]");
      const requests = Array.isArray(stored) ? stored : [];
      requests.push({ ...parsed.data, offer, wantMaterials: defaultWantMaterials, createdAt: new Date().toISOString() });
      localStorage.setItem("investorRequests", JSON.stringify(requests));
    } catch {
      /* no-op 
    }
    setTimeout(() => setState("success"), 400);
  };*/

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border">
        {state === "success" ? (
          <div className="py-10 text-center">
            <IconHeading
              as="h3"
              icon={
                <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-6" />
              }
              className="headerh3 mb-3"
            >
              Спасибо
            </IconHeading>
            <p className="p-normal text-muted-foreground max-w-md mx-auto">
              Мы изучим информацию и свяжемся с вами для знакомства и
              предметного обсуждения.
            </p>
            <Button
              variant="siteOutline"
              className="mt-8"
              onClick={() => onOpenChange(false)}
            >
              Закрыть
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-montserrat-semibold text-xl sm:text-2xl">
                Начать закрытый разговор
              </DialogTitle>
              <DialogDescription className="font-montserrat">
                Расскажите, в каком качестве вы рассматриваете VCENTRE.
                Финансовые материалы предоставляются после знакомства.
              </DialogDescription>
            </DialogHeader>

            <form /*onSubmit={handleSubmit} */ className="space-y-5 mt-2">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="inv-name">Имя *</Label>
                  <Input
                    id="inv-name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Ваше имя"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-company">Компания / фонд</Label>
                  <Input
                    id="inv-company"
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Название"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-role">Роль *</Label>
                  <Input
                    id="inv-role"
                    value={form.role}
                    onChange={set("role")}
                    placeholder="Инвестор, предприниматель, директор"
                    maxLength={120}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-contact">Контакт *</Label>
                  <Input
                    id="inv-contact"
                    value={form.contact}
                    onChange={set("contact")}
                    placeholder="Телефон, email или Telegram"
                    maxLength={255}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Тип предложения</Label>
                <div className="flex flex-wrap gap-2">
                  {offerOptions.map((o) => (
                    <Button
                      key={o.value}
                      type="button"
                      variant={offer === o.value ? "site" : "siteOutline"}
                      onClick={() => setOffer(o.value)}
                      aria-pressed={offer === o.value}
                    >
                      {o.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="inv-interest">
                  Что вас заинтересовало в VCENTRE? *
                </Label>
                <Textarea
                  id="inv-interest"
                  rows={4}
                  value={form.interest}
                  onChange={set("interest")}
                  maxLength={1500}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="inv-range">
                    Диапазон потенциального участия
                  </Label>
                  <Input
                    id="inv-range"
                    value={form.range}
                    onChange={set("range")}
                    placeholder="Необязательно"
                    maxLength={120}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-link">Сайт / LinkedIn / Telegram</Label>
                  <Input
                    id="inv-link"
                    value={form.link}
                    onChange={set("link")}
                    placeholder="Необязательно"
                    maxLength={300}
                  />
                </div>
              </div>

              <Button
                type="submit"
                variant="site"
                className="w-full"
                disabled={state === "sending"}
              >
                {state === "sending" ? "Отправляем..." : "Начать разговор"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvestorContactDialog;
